import { memo, useState } from "react";
import { Button } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { DatePicker, DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { format, parseISO } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const monthLookup = {
  "01": "January",
  "1": "January",
  "02": "February",
  "2": "February",
  "03": "March",
  "3": "March",
  "04": "April",
  "4": "April",
  "05": "May",
  "5": "May",
  "06": "June",
  "6": "June",
  "07": "July",
  "7": "July",
  "08": "August",
  "8": "August",
  "09": "September",
  "9": "September",
  "10": "October",
  "11": "November",
  "12": "December"
};

const getDateDisplay = (date) => {
  if (date === undefined || date === null || date === '') {
    return ""
  }
  if (typeof date === "string") {
    if (date.length === 4 && /^[0-9]+$/.test(date)) {
      return `${date}`;
    }
    let dateSplit = date.split("-");
    if (dateSplit[0].length === 4 && /^[0-9]+$/.test(dateSplit[0])) {
      if (dateSplit.length >= 2 && monthLookup[dateSplit[1]]) {
        if (dateSplit.length === 3 && date.length === 10 && /^[0-9]+$/.test(dateSplit[2])) {
          if (dateSplit[2] > 9) {
            return monthLookup[dateSplit[1]] + " " + dateSplit[2] + ", " + dateSplit[0];
          } else if (dateSplit[2] > 0) {
            return monthLookup[dateSplit[1]] + " " + dateSplit[2][1] + ", " + dateSplit[0];
          }
        } else if (dateSplit.length === 2 && date.length === 7) {
          return monthLookup[dateSplit[1]] + " " + dateSplit[0];
        }
      }
    }
    return `${date} [ERROR: date data not ISO format YYYY-MM-DD]`;
  }
  if (!isNaN(date) && typeof date === "number") {
    return `${date} [ERROR: date data not in string datatype with ISO format YYYY-MM-DD]`
  }
  return "[ERROR: date data not interpretable.]";
}

const getDateTimeDisplay = (dateTime) => {
  if (dateTime === undefined || dateTime === null || dateTime === '') {
    return ""
  }
  if (typeof dateTime === "string") {
    if (parseISO(dateTime) !== "Invalid Date") {
      let dateTimeSplit = dateTime.split('T');
      if (dateTimeSplit.length === 1) {
        return getDateDisplay(dateTime);
      }
      if (dateTimeSplit.length === 2) {
        let dateDisplay = getDateDisplay(dateTimeSplit[0]);
        let timeDisplay = dateTimeSplit[1];
        return `${dateDisplay} at ${timeDisplay}`;
      }
    }
    return `${dateTime} [ERROR: dateTime data not ISO format YYYY-MM-DDThh:mm:ss.sTZD]`;
  }
  if (!isNaN(dateTime) && typeof dateTime === "number") {
    return `${dateTime} [ERROR: dateTime data not in string datatype with ISO format YYYY-MM-DDThh:mm:ss.sTZD]`
  }
  return "[ERROR: dateTime data not interpretable.]";
}

const DateEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, width }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const CalendarModal = () => {
    return <div className="calendarModal"><MuiPickersUtilsProvider utils={DateFnsUtils}>
      <DatePicker
        format="yyyy-MM-dd"
        emptyLabel="yyyy-MM-dd"
        value={startingValue ? parseISO(startingValue, "yyyy-MM-dd") : null}
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onChange={(e) => {
          setResourceState(prevState => { return { ...prevState, [elementName]: format(e, "yyyy-MM-dd") } });
        }}
      />
    </MuiPickersUtilsProvider></div>
  }

  return <div>
    <TextField style={{ width: "100%", marginTop: "12px", maxWidth: width ? width : "150px" }}
      className="inputField" type='text' label={fieldLabel + " (YYYY-MM-DD)"} size="small" variant='outlined'
      value={startingValue || ""}
      onChange={(e) => { 
        setResourceState(prevState => { return { ...prevState, [elementName]: e.target.value } });
         }} />
    <Button style={{ marginTop: "18px", marginLeft: "6px", fontSize: "28px", padding: "0px", backgroundColor: "white" }} className="calendarButton"
      content="📅" onClick={() => { setCalendarOpen(true) }} />
    {calendarOpen && <CalendarModal />}
  </div>
})

const DateTimeEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const CalendarModal = () => {
    return <div className="calendarModal"><MuiPickersUtilsProvider utils={DateFnsUtils}>
      <DateTimePicker
        format="yyyy-MM-dd HH:mm:ss"
        value={startingValue ? parseISO(startingValue, "yyyy-MM-dd HH:mm:ss") : null}
        emptyLabel="yyyy-MM-dd HH:mm:ss"
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onChange={(e) => {
          setResourceState(prevState => { return { ...prevState, [elementName]: format(e, "yyyy-MM-dd") + "T" + format(e, "HH:mm:ss") } });
        }}
      />
    </MuiPickersUtilsProvider></div>
  }

  return <div>
    <TextField style={{ width: "100%", marginTop: "12px", maxWidth: "300px" }}
      className="inputField" type='text' label={fieldLabel} size="small" variant='outlined'
      value={startingValue || ""}
      onChange={(e) => {
        setResourceState(prevState => { return { ...prevState, [elementName]: e.target.value } });
      }} />
    <Button style={{ marginTop: "18px", marginLeft: "6px", fontSize: "28px", padding: "0px", backgroundColor: "white" }} className="calendarButton"
      content="📅" onClick={() => { setCalendarOpen(true) }} />
    {calendarOpen && <CalendarModal />}
  </div>
})

const displayStringFromFHIRPeriod = (period) => {
  if (period === undefined || period === null || period === '') {
    return ""
  }
  if (typeof period === 'object') {
    if (period.start || period.end) {
      let startDisplay = getDateTimeDisplay(period.start) || "[No start date.]";
      let endDisplay = getDateTimeDisplay(period.end) || "[No end date.]";
      return `${startDisplay} to ${endDisplay}`;
    }
  }
  if (typeof period === 'string') {
    return `${period} [ERROR: Period data not in expected Period datatype.]`;
  }
  return "[ERROR: Period data not interpretable.]";
}

const PeriodEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, inTableCell, startCollapsed }) => {
  if (!startingValue) {
    startingValue = "";
  }

  const [startCalendarOpen, setStartCalendarOpen] = useState(false);
  const [endCalendarOpen, setEndCalendarOpen] = useState(false);
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  const CalendarStartModal = () => {
    return <div className="calendarModal"><MuiPickersUtilsProvider utils={DateFnsUtils}>
      <DateTimePicker
        format="yyyy-MM-dd HH:mm:ss"
        value={startingValue?.start ? parseISO(startingValue.start, "yyyy-MM-dd HH:mm:ss") : null}
        emptyLabel="yyyy-MM-dd HH:mm:ss"
        open={startCalendarOpen}
        onClose={() => setStartCalendarOpen(false)}
        onChange={(e) => {
          let newStart = format(e, "yyyy-MM-dd") + "T" + format(e, "HH:mm:ss");
          let newPeriod = { start: newStart, end: "" };
          if (startingValue.end) { newPeriod.end = startingValue.end; } else { delete newPeriod.end; }
          setResourceState(prevState => { return { ...prevState, [elementName]: newPeriod } });
        }}
      />
    </MuiPickersUtilsProvider></div>
  }

  const CalendarEndModal = () => {
    return <div className="calendarModal"><MuiPickersUtilsProvider utils={DateFnsUtils}>
      <DateTimePicker
        format="yyyy-MM-dd HH:mm:ss"
        value={startingValue?.end ? parseISO(startingValue.end, "yyyy-MM-dd HH:mm:ss") : null}
        emptyLabel="yyyy-MM-dd HH:mm:ss"
        open={endCalendarOpen}
        onClose={() => setEndCalendarOpen(false)}
        onChange={(e) => {
          let newEnd = format(e, "yyyy-MM-dd") + "T" + format(e, "HH:mm:ss");
          let newPeriod = { start: "", end: newEnd };
          if (startingValue.start) { newPeriod.start = startingValue.start; } else { delete newPeriod.start; }
          setResourceState(prevState => { return { ...prevState, [elementName]: newPeriod } });
        }}
      />
    </MuiPickersUtilsProvider></div>
  }

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRPeriod(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <><div className="inlinegrouping">
      <b>{fieldLabel}: </b>
      {inTableCell && <br />}
      <TextField style={{ width: "50%", marginTop: "16px", maxWidth: "300px" }}
        className="inputField" type='text' label="Start Date/Time" size="small" variant='outlined'
        value={startingValue.start || ""}
        onChange={(e) => {
          let newStart = e.target.value;
          let newPeriod = { start: newStart, end: "" };
          if (startingValue.end) { newPeriod.end = startingValue.end; } else { delete newPeriod.end; }
          if (startingValue.end || e.target.value) {
            setResourceState(prevState => { return { ...prevState, [elementName]: newPeriod } });
          } else {
            setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
          }
        }} />
      <Button className="calendarButton"
        style={{ marginTop: "18px", marginLeft: "6px", fontSize: "28px", padding: "0px", backgroundColor: "white" }}
        content="📅" onClick={() => { setStartCalendarOpen(true) }} />
      {startCalendarOpen && <CalendarStartModal />}
      {((startingValue.start && startingValue.end) && startingValue.start > startingValue.end) &&
        <p><b>Start date should NOT be later than end date!!!!!!!!!!!!!!!!!!!!!!!!!!!!</b></p>}
      <TextField style={inTableCell ? { width: "50%", marginTop: "16px", maxWidth: "300px" }
        : { width: "50%", marginLeft: "114px", marginTop: "16px", maxWidth: "300px" }}
        className="inputField" type='text' label="End Date/Time" size="small" variant='outlined'
        value={startingValue?.end || ""}
        onChange={(e) => {
          let newEnd = e.target.value;
          let newPeriod = { start: "", end: newEnd };
          if (startingValue.start) { newPeriod.start = startingValue.start; } else { delete newPeriod.start; }
          if (startingValue.start || e.target.value) {
            setResourceState(prevState => { return { ...prevState, [elementName]: newPeriod } });
          } else {
            setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
          }
        }} />
      <Button className="calendarButton"
        style={{ marginTop: "18px", marginLeft: "6px", fontSize: "28px", padding: "0px", backgroundColor: "white" }}
        content="📅" onClick={() => { setEndCalendarOpen(true) }} />
      {endCalendarOpen && <CalendarEndModal />}
    </div>
    </>
  }
});

export default PeriodEntry;
export { DateEntry, DateTimeEntry, displayStringFromFHIRPeriod, getDateDisplay, getDateTimeDisplay };